/** Horní lišta editoru layoutu (jen admin v edit mode): undo/redo, uložit, zahodit, reset, konec. */

import { Redo2, RotateCcw, Save, Undo2, X } from "lucide-react";
import React from "react";
import { toast } from "sonner";
import { normalize } from "@globals/layouts";
import { putLayout, resetLayout } from "@client/api/layouts";
import { useLayoutStore } from "@client/store/layout";
import { useLayoutEditor } from "@client/store/layoutEditor";
import { useUIStore } from "@client/store/ui";

const btn = "inline-flex items-center gap-1 rounded border border-input bg-background px-2 py-1 text-xs hover:bg-accent disabled:opacity-40";

export const EditToolbar: React.FC = () => {
  const { draftKey, draftName, doc, dirty, stop, conflictVersion } = useLayoutEditor();
  const setEditMode = useUIStore((s) => s.setEditMode);
  const layoutStore = useLayoutStore();

  if (!draftKey || !doc) return null;
  const committed = layoutStore.byKey.get(draftKey);

  const exit = () => {
    if (dirty && !window.confirm("Zahodit neuložené změny?")) return;
    stop();
    setEditMode(false);
  };

  const save = async () => {
    try {
      // Úklid až teď (ne během editace): zahodí prázdné kontejnery/gridy a sbalí jedno-dítě kontejnery.
      const cleaned = { ...doc, root: normalize(doc.root) } as typeof doc;
      const res = await putLayout(draftKey, { name: draftName, version: committed?.version ?? 0, doc: cleaned });
      if (!res.success) {
        toast.error(res.message || "Uložení selhalo.");
        return;
      }
      const newVersion = res.responseObject?.version ?? (committed?.version ?? 0) + 1;
      // Metadata řádku se uložením nemění, jen verze a obsah — proto se zbytek entry přebírá.
      if (committed) layoutStore.setLocal(draftKey, { ...committed, doc: cleaned, version: newVersion, name: draftName });
      useLayoutEditor.setState({ dirty: false });
      // re-base draft verze pro další uložení
      toast.success("Uloženo (projeví se všem).");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Uložení selhalo.");
    }
  };

  const reset = async () => {
    if (!committed) return;
    // Bez tovární kopie není na co resetovat — server by vrátil 409, tak se ani neptáme.
    if (!committed.hasSeed) {
      toast.error("Pro tuhle plochu není tovární verze.");
      return;
    }
    if (!window.confirm("Obnovit tovární rozložení (zahodí úpravy pro všechny)?")) return;

    const res = await resetLayout(draftKey, committed.version);
    if (!res.success) {
      toast.error(res.message || "Obnovení selhalo.");
      return;
    }
    // Server vrací jen verzi; obsah si dotáhneme, ať v editoru sedí přesně to, co je v DB.
    await layoutStore.refetchOne(draftKey);
    const fresh = useLayoutStore.getState().byKey.get(draftKey);
    if (fresh) useLayoutEditor.getState().beginEdit(draftKey, fresh.name, fresh.doc);
    toast.success("Obnoveno na tovární verzi.");
  };

  const temporal = useLayoutEditor.temporal.getState();

  /** Cizí verze převzít = zahodit vlastní práci, tak ať to člověk odklepne. */
  const prevzitCizi = () => {
    if (!window.confirm("Zahodit své úpravy a převzít verzi, kterou uložil někdo jiný?")) return;
    const fresh = useLayoutStore.getState().byKey.get(draftKey);
    if (fresh) useLayoutEditor.getState().rebase(fresh.name, fresh.doc);
  };

  return (
    <div className="sticky top-0 z-30 mb-2 flex flex-col gap-2 rounded-md border border-primary/40 bg-card px-3 py-2 shadow-sm">
      {/*
        Rozdělaný draft se při cizím uložení NEPŘEPISUJE — jen se zamkne ukládání. Dřív se
        committed verze ve storu tiše posunula a další „Uložit" cizí změnu přemazalo.
      */}
      {conflictVersion !== null && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-status-warning/50 bg-status-warning/10 px-2 py-1 text-xs">
          <span>Rozložení mezitím uložil někdo jiný (v{conflictVersion}). Tvoje změny se neuloží.</span>
          <div className="flex-1" />
          <button className={btn} onClick={prevzitCizi}>
            Převzít jeho verzi
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold text-primary">Editace: {draftName}</span>
      <div className="flex-1" />
      <button className={btn} onClick={() => temporal.undo()} disabled={temporal.pastStates.length === 0} title="Zpět">
        <Undo2 className="h-3.5 w-3.5" />
      </button>
      <button className={btn} onClick={() => temporal.redo()} disabled={temporal.futureStates.length === 0} title="Vpřed">
        <Redo2 className="h-3.5 w-3.5" />
      </button>
      <button className={btn} onClick={reset} title="Reset na výchozí">
        <RotateCcw className="h-3.5 w-3.5" /> Reset
      </button>
      <button className={btn} onClick={exit}>
        <X className="h-3.5 w-3.5" /> Konec
      </button>
      <button
        className={btn + " border-primary bg-primary text-primary-foreground"}
        onClick={save}
        disabled={!dirty || conflictVersion !== null}
        title={conflictVersion !== null ? "Nejdřív vyřeš cizí změnu." : undefined}
      >
        <Save className="h-3.5 w-3.5" /> Uložit
      </button>
      </div>
    </div>
  );
};

export default EditToolbar;
